import { useEffect, useMemo, useState } from 'react'
import type { CSSProperties, FormEvent } from 'react'

type User = {
  email: string
  display_name?: string | null
  account_status?: string
  is_admin?: number | boolean
}

type AuthPayload = {
  authenticated?: boolean
  accessToken?: string
  user?: User
  error?: string
  message?: string
}

type CompletionPayload = {
  choices?: Array<{ message?: { content?: unknown } }>
  response?: unknown
  result?: unknown
  error?: string
  message?: string
}

type DesktopProject = {
  canceled?: boolean
  name?: string
  path?: string
  files?: string[]
}

declare global {
  interface Window {
    hairlessDesktop?: {
      platform: string
      openProject: () => Promise<DesktopProject>
      readProjectFile: (relativePath: string) => Promise<{ path: string; content: string }>
      writeProjectFile: (relativePath: string, content: string) => Promise<{ ok: boolean; path: string }>
    }
  }
}

const API_URL = ((import.meta as ImportMeta & { env?: Record<string, string | undefined> }).env?.VITE_HAIRLESS_API_URL
  ?? 'https://hairless-saas-api.yk445kauod.workers.dev').replace(/\/$/, '')
const TOKEN_KEY = 'hairless_access_token'
const CLOUDFLARE_MODEL = '@cf/meta/llama-3.2-3b-instruct'

async function requestJson<T>(path: string, init: RequestInit = {}, token?: string): Promise<T> {
  const headers = new Headers(init.headers)
  headers.set('content-type', 'application/json')
  if (token) headers.set('authorization', `Bearer ${token}`)
  const response = await fetch(`${API_URL}${path}`, { ...init, headers, credentials: 'include' })
  const body = await response.json().catch(() => ({})) as T & { error?: string; message?: string }
  if (!response.ok) throw new Error(body.error ?? body.message ?? `request_failed_${response.status}`)
  return body
}

function readableError(error: unknown): string {
  const code = error instanceof Error ? error.message : 'request_failed'
  const messages: Record<string, string> = {
    invalid_credentials: 'البريد الإلكتروني أو كلمة المرور غير صحيحة.',
    email_already_registered: 'البريد الإلكتروني مسجّل بالفعل.',
    invalid_email: 'اكتب بريدًا إلكترونيًا صحيحًا.',
    password_too_weak: 'كلمة المرور لازم تكون 10 أحرف على الأقل وتحتوي رقمًا.',
    account_locked: 'المحاولات الكثيرة قفلت الحساب مؤقتًا. جرّب بعد شوية.',
    activation_required: 'الحساب محتاج كود تفعيل من الأدمن قبل استخدام الوكيل.',
    provider_connection_required: 'المزوّد ده محتاج توصيل BYOK من Dashboard.',
    provider_api_key_required: 'المزوّد ده محتاج API key محفوظة في توصيلاتك.',
    cloudflare_ai_not_configured: 'Cloudflare Workers AI مش متوصل في البيئة الحالية.',
  }
  return messages[code] ?? 'حصلت مشكلة. راجع البيانات وحاول تاني.'
}

function assistantText(payload: CompletionPayload): string {
  const content = payload.choices?.[0]?.message?.content
  if (typeof content === 'string') return content
  if (Array.isArray(content)) return content.map((item) => typeof item === 'string' ? item : (item as { text?: string }).text ?? '').join('')
  if (typeof payload.response === 'string') return payload.response
  if (typeof payload.result === 'string') return payload.result
  return JSON.stringify(payload, null, 2)
}

export function PagesWebApp() {
  const [token, setToken] = useState(() => sessionStorage.getItem(TOKEN_KEY) ?? '')
  const [user, setUser] = useState<User | undefined>()
  const [checking, setChecking] = useState(true)
  const [authMode, setAuthMode] = useState<'login' | 'register'>('login')
  const [email, setEmail] = useState('')
  const [displayName, setDisplayName] = useState('')
  const [password, setPassword] = useState('')
  const [authBusy, setAuthBusy] = useState(false)
  const [authError, setAuthError] = useState('')
  const [providerId, setProviderId] = useState('cloudflare')
  const [model, setModel] = useState(CLOUDFLARE_MODEL)
  const [prompt, setPrompt] = useState('راجع الفكرة دي كمهندس برمجيات واقترح خطوات تنفيذ عملية.')
  const [answer, setAnswer] = useState('')
  const [aiBusy, setAiBusy] = useState(false)
  const [aiError, setAiError] = useState('')
  const [localProject, setLocalProject] = useState<DesktopProject | undefined>()
  const [projectBusy, setProjectBusy] = useState(false)
  const [projectError, setProjectError] = useState('')

  useEffect(() => {
    let cancelled = false
    if (!token) {
      setChecking(false)
      return () => { cancelled = true }
    }
    void requestJson<AuthPayload>('/api/auth/me', {}, token)
      .then((payload) => {
        if (!cancelled) setUser(payload.user)
      })
      .catch(() => {
        sessionStorage.removeItem(TOKEN_KEY)
        if (!cancelled) {
          setToken('')
          setUser(undefined)
        }
      })
      .finally(() => {
        if (!cancelled) setChecking(false)
      })
    return () => { cancelled = true }
  }, [token])

  const providerName = useMemo(() => providerId === 'pollinations' ? 'Pollinations AI' : 'Cloudflare Workers AI', [providerId])

  async function submitAuth(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    setAuthBusy(true)
    setAuthError('')
    try {
      const payload = await requestJson<AuthPayload>(`/api/auth/${authMode}`, {
        method: 'POST',
        body: JSON.stringify({ email, password, ...(authMode === 'register' ? { displayName } : {}) }),
      })
      if (!payload.accessToken || !payload.user) throw new Error('auth_response_incomplete')
      sessionStorage.setItem(TOKEN_KEY, payload.accessToken)
      setToken(payload.accessToken)
      setUser(payload.user)
      setPassword('')
    } catch (error) {
      setAuthError(readableError(error))
    } finally {
      setAuthBusy(false)
    }
  }

  async function complete(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (!prompt.trim()) return
    setAiBusy(true)
    setAiError('')
    setAnswer('')
    try {
      const payload = await requestJson<CompletionPayload>('/api/ai/complete', {
        method: 'POST',
        body: JSON.stringify({
          providerId,
          model: model.trim() || undefined,
          messages: [
            { role: 'system', content: 'أنت Hairless، مساعد برمجي وكيل. أجب بالعربية المصرية بوضوح وبخطوات قابلة للتنفيذ.' },
            { role: 'user', content: prompt },
          ],
          temperature: 0.2,
          max_tokens: 1200,
        }),
      }, token)
      setAnswer(assistantText(payload))
    } catch (error) {
      setAiError(readableError(error))
    } finally {
      setAiBusy(false)
    }
  }

  async function logout() {
    const current = token
    sessionStorage.removeItem(TOKEN_KEY)
    setToken('')
    setUser(undefined)
    if (current) await requestJson('/api/auth/logout', { method: 'POST' }, current).catch(() => undefined)
  }

  async function openLocalProject() {
    if (!window.hairlessDesktop) return
    setProjectBusy(true)
    setProjectError('')
    try {
      const project = await window.hairlessDesktop.openProject()
      if (!project.canceled) setLocalProject(project)
    } catch (error) {
      setProjectError(error instanceof Error ? error.message : 'تعذر فتح مجلد المشروع.')
    } finally {
      setProjectBusy(false)
    }
  }

  if (checking) return <div style={styles.loading}>Hairless بيجهّز مساحتك…</div>

  if (!user) {
    return (
      <main style={styles.authPage} dir="rtl">
        <section style={styles.authCard}>
          <div style={styles.brandRow}><span style={styles.brandMark}>HL</span><span style={styles.brandName}>HAIRLESS</span></div>
          <p style={styles.eyebrow}>THE AGENTIC CODING HARNESS</p>
          <h1 style={styles.authTitle}>{authMode === 'login' ? 'سجّل دخولك إلى Hairless' : 'أنشئ حساب Hairless'}</h1>
          <p style={styles.muted}>حسابك خاص بالمنصة ومُدار بالكامل داخل Hairless Worker، بدون Google أو GitHub أو مزوّد Auth خارجي.</p>
          <form onSubmit={submitAuth} style={styles.form}>
            {authMode === 'register' && <label style={styles.label}>الاسم<input style={styles.input} value={displayName} onChange={(event) => setDisplayName(event.target.value)} autoComplete="name" required /></label>}
            <label style={styles.label}>البريد الإلكتروني<input style={styles.input} type="email" value={email} onChange={(event) => setEmail(event.target.value)} autoComplete="email" required /></label>
            <label style={styles.label}>كلمة المرور<input style={styles.input} type="password" value={password} onChange={(event) => setPassword(event.target.value)} autoComplete={authMode === 'login' ? 'current-password' : 'new-password'} required /></label>
            {authError && <div style={styles.error} role="alert">{authError}</div>}
            <button style={styles.primaryButton} type="submit" disabled={authBusy}>{authBusy ? 'جارِ المعالجة…' : authMode === 'login' ? 'دخول' : 'إنشاء الحساب'}</button>
          </form>
          <button style={styles.linkButton} type="button" onClick={() => { setAuthMode(authMode === 'login' ? 'register' : 'login'); setAuthError('') }}>{authMode === 'login' ? 'لسه معندكش حساب؟ اعمل حساب' : 'عندك حساب؟ سجّل دخول'}</button>
        </section>
      </main>
    )
  }

  return (
    <div style={styles.app} dir="rtl">
      <header style={styles.topbar}>
        <div style={styles.brandRow}><span style={styles.brandMark}>HL</span><div><strong style={styles.brandName}>HAIRLESS</strong><small style={styles.brandSub}>the agentic coding harness</small></div></div>
        <div style={styles.account}><span>{user.display_name || user.email}</span><button style={styles.logoutButton} type="button" onClick={() => void logout()}>خروج</button></div>
      </header>
      {user.account_status !== 'active' && user.is_admin !== 1 && <div style={styles.pending}>الحساب مستني تفعيل الأدمن. تقدر تشوف المساحة، لكن طلبات الوكيل هتحتاج تفعيل الحساب.</div>}
      <div style={styles.layout}>
        <aside style={styles.sidebar}>
          <div style={styles.sidePanel}><span style={styles.sideLabel}>مساحة Hairless</span><h2 style={styles.sideTitle}>Browser workspace</h2><p style={styles.muted}>نفس مشروع Hairless الأساسي، متصل مباشرةً بالـ Worker API.</p></div>
          <div style={styles.sidePanel}><span style={styles.sideLabel}>المزوّد الحالي</span><strong style={styles.providerValue}>{providerName}</strong><p style={styles.muted}>{providerId === 'pollinations' ? 'مسار مجاني اختياري بدون مفتاح.' : 'Workers AI مربوط داخل Cloudflare.'}</p></div>
          <div style={styles.sidePanel}><span style={styles.sideLabel}>الحساب</span><strong style={styles.providerValue}>{user.account_status === 'active' ? 'Active' : 'Pending'}</strong><p style={styles.muted}>{user.email}</p></div>
        </aside>
        <main style={styles.main}>
          <section style={styles.hero}><p style={styles.eyebrow}>HAIRLESS / WEB</p><h1 style={styles.heroTitle}>اكتب، راجع، وخطّط للكود من نفس مساحة Hairless.</h1><p style={styles.heroCopy}>واجهة الويب دي جزء من المشروع الأصلي، وتستخدم نفس المصادقة وWorker وكتالوج المزوّدين. تطبيقات Desktop هتضيف الوصول الآمن لملفات المشروع المحلي.</p></section>
          {window.hairlessDesktop && <section style={styles.projectCard}>
            <div style={styles.sectionHead}><div><span style={styles.sideLabel}>Desktop workspace</span><h2 style={styles.sectionTitle}>مشروعك المحلي</h2></div><span style={styles.liveDot}>{localProject ? '● مفتوح' : '○ غير محدد'}</span></div>
            <p style={styles.muted}>{localProject ? `تم فتح ${localProject.name} — ${localProject.files?.length ?? 0} ملف قابل للفحص.` : 'اختار مجلد مشروعك عشان Hairless يشتغل على الملفات المحلية من نفس التطبيق.'}</p>
            <div style={styles.projectActions}><button style={styles.primaryButton} type="button" onClick={() => void openLocalProject()} disabled={projectBusy}>{projectBusy ? 'جارِ فتح المجلد…' : localProject ? 'اختيار مشروع آخر' : 'فتح مجلد مشروع'}</button>{localProject?.path && <code style={styles.projectPath}>{localProject.path}</code>}</div>
            {projectError && <div style={styles.error} role="alert">{projectError}</div>}
          </section>}
          <section style={styles.composerCard}>
            <div style={styles.sectionHead}><div><span style={styles.sideLabel}>Agent request</span><h2 style={styles.sectionTitle}>محادثة الوكيل البرمجي</h2></div><span style={styles.liveDot}>● متصل</span></div>
            <form onSubmit={complete}>
              <textarea style={styles.textarea} value={prompt} onChange={(event) => setPrompt(event.target.value)} rows={7} placeholder="اكتب طلبك البرمجي هنا…" />
              <div style={styles.controls}>
                <label style={styles.controlLabel}>المزوّد<select style={styles.select} value={providerId} onChange={(event) => { const next = event.target.value; setProviderId(next); setModel(next === 'pollinations' ? 'openai' : CLOUDFLARE_MODEL) }}><option value="cloudflare">Cloudflare Workers AI</option><option value="pollinations">Pollinations AI</option></select></label>
                <label style={styles.controlLabel}>الموديل<input style={styles.input} value={model} onChange={(event) => setModel(event.target.value)} /></label>
                <button style={styles.primaryButton} type="submit" disabled={aiBusy || user.account_status !== 'active'}>{aiBusy ? 'الوكيل بيفكّر…' : 'إرسال للوكيل'}</button>
              </div>
            </form>
            {aiError && <div style={styles.error} role="alert">{aiError}</div>}
            {answer && <div style={styles.answer}><div style={styles.sideLabel}>Hairless response</div><pre style={styles.answerText}>{answer}</pre></div>}
          </section>
        </main>
      </div>
    </div>
  )
}

const styles: Record<string, CSSProperties> = {
  loading: { minHeight: '100vh', display: 'grid', placeItems: 'center', background: '#131313', color: '#ff6b2c', fontFamily: 'IBM Plex Sans Arabic, system-ui, sans-serif' },
  authPage: { minHeight: '100vh', display: 'grid', placeItems: 'center', padding: 24, boxSizing: 'border-box', background: 'radial-gradient(circle at 50% 0%, #2a1a13, #171717 48%, #101010)', fontFamily: 'IBM Plex Sans Arabic, system-ui, sans-serif' },
  authCard: { width: 'min(100%, 460px)', padding: 32, boxSizing: 'border-box', border: '1px solid #44352e', borderRadius: 24, background: '#1b1b1b', color: '#fff', boxShadow: '0 28px 100px rgba(0,0,0,.45)' },
  brandRow: { display: 'flex', alignItems: 'center', gap: 12 },
  brandMark: { display: 'grid', placeItems: 'center', width: 42, height: 42, borderRadius: 12, background: '#ff6b2c', color: '#181818', fontWeight: 900, letterSpacing: 1 },
  brandName: { color: '#fff', letterSpacing: 2, fontSize: 15 },
  brandSub: { display: 'block', color: '#8e8e8e', fontSize: 11, marginTop: 3 },
  eyebrow: { color: '#ff7d46', letterSpacing: 2.5, fontSize: 11, fontWeight: 800, margin: '24px 0 8px' },
  authTitle: { margin: '0 0 10px', fontSize: 28, lineHeight: 1.25 },
  muted: { color: '#9d9d9d', fontSize: 13, lineHeight: 1.7, margin: 0 },
  form: { display: 'grid', gap: 15, marginTop: 22 },
  label: { display: 'grid', gap: 7, color: '#d8d8d8', fontSize: 13 },
  input: { width: '100%', boxSizing: 'border-box', border: '1px solid #4a443f', borderRadius: 10, padding: '11px 12px', background: '#111', color: '#fff', outline: 'none', fontSize: 14 },
  primaryButton: { border: 0, borderRadius: 10, padding: '12px 18px', background: '#ff6b2c', color: '#171717', cursor: 'pointer', fontWeight: 900, whiteSpace: 'nowrap' },
  linkButton: { width: '100%', border: 0, background: 'transparent', color: '#ff9d76', cursor: 'pointer', padding: '15px 0 0', fontSize: 13 },
  error: { marginTop: 14, padding: 11, borderRadius: 9, background: '#3a1c1c', color: '#ffb4a3', fontSize: 13, lineHeight: 1.5 },
  app: { minHeight: '100vh', background: '#121212', color: '#f5f5f5', fontFamily: 'IBM Plex Sans Arabic, system-ui, sans-serif' },
  topbar: { minHeight: 72, padding: '12px clamp(18px, 4vw, 56px)', boxSizing: 'border-box', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid #2a2a2a', background: '#171717' },
  account: { display: 'flex', alignItems: 'center', gap: 12, color: '#c4c4c4', fontSize: 12 },
  logoutButton: { border: '1px solid #4d3930', borderRadius: 999, padding: '7px 13px', background: 'transparent', color: '#ff9a70', cursor: 'pointer', fontSize: 12 },
  pending: { padding: '9px 18px', background: '#3c2717', color: '#ffd2b6', textAlign: 'center', fontSize: 12 },
  layout: { maxWidth: 1320, margin: '0 auto', padding: 'clamp(20px, 5vw, 56px)', display: 'grid', gridTemplateColumns: '260px minmax(0, 1fr)', gap: 34 },
  sidebar: { display: 'grid', alignContent: 'start', gap: 14 },
  sidePanel: { padding: 18, border: '1px solid #2d2d2d', borderRadius: 16, background: '#191919' },
  sideLabel: { display: 'block', color: '#ff7d46', textTransform: 'uppercase', letterSpacing: 1.5, fontSize: 10, fontWeight: 900, marginBottom: 8 },
  sideTitle: { margin: 0, fontSize: 17 },
  providerValue: { display: 'block', fontSize: 15, color: '#f3f3f3' },
  main: { minWidth: 0 },
  hero: { padding: '8px 0 28px' },
  heroTitle: { maxWidth: 720, margin: 0, fontSize: 'clamp(30px, 5vw, 58px)', lineHeight: 1.08, letterSpacing: -1.5 },
  heroCopy: { maxWidth: 720, color: '#a6a6a6', lineHeight: 1.8, fontSize: 15, margin: '18px 0 0' },
  composerCard: { padding: 'clamp(18px, 3vw, 30px)', border: '1px solid #34302d', borderRadius: 22, background: 'linear-gradient(145deg, #1f1b18, #181818)' },
  projectCard: { marginBottom: 18, padding: 'clamp(18px, 3vw, 26px)', border: '1px solid #3b3029', borderRadius: 18, background: '#191614' },
  projectActions: { display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 12, marginTop: 16 },
  projectPath: { maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', direction: 'ltr', color: '#c6b0a4', fontSize: 12 },
  sectionHead: { display: 'flex', justifyContent: 'space-between', alignItems: 'start', gap: 16, marginBottom: 18 },
  sectionTitle: { margin: 0, fontSize: 21 },
  liveDot: { color: '#9ce2b4', fontSize: 12 },
  textarea: { width: '100%', boxSizing: 'border-box', resize: 'vertical', minHeight: 150, border: '1px solid #4a403a', borderRadius: 14, padding: 16, background: '#111', color: '#fff', outline: 'none', font: 'inherit', lineHeight: 1.7 },
  controls: { display: 'grid', gridTemplateColumns: 'minmax(150px, .8fr) minmax(200px, 1.2fr) auto', gap: 12, alignItems: 'end', marginTop: 14 },
  controlLabel: { display: 'grid', gap: 7, color: '#aaa', fontSize: 11 },
  select: { width: '100%', boxSizing: 'border-box', border: '1px solid #4a443f', borderRadius: 10, padding: '11px 12px', background: '#111', color: '#fff', font: 'inherit' },
  answer: { marginTop: 20, padding: 18, borderRadius: 14, border: '1px solid #3b332e', background: '#101010' },
  answerText: { whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', margin: 0, color: '#e9e1dc', font: '14px/1.8 ui-monospace, SFMono-Regular, Menlo, monospace', direction: 'ltr', textAlign: 'left' },
}
